import { Card, Badge } from '@/core/ui/components';
import type { TenantDashboardSubscription } from '@yawi/types';
import { CreditCard, Calendar, Package } from 'lucide-react';

interface SubscriptionCardProps {
  subscription: TenantDashboardSubscription | null;
}

const STATUS_BADGES: Record<string, { variant: 'success' | 'warning' | 'error' | 'default' | 'purple'; label: string }> = {
  TRIAL: { variant: 'purple', label: 'Trial' },
  ACTIVE: { variant: 'success', label: 'Activa' },
  PAST_DUE: { variant: 'warning', label: 'Pago pendiente' },
  SUSPENDED: { variant: 'error', label: 'Suspendida' },
  CANCELLED: { variant: 'error', label: 'Cancelada' },
};

export function SubscriptionCard({ subscription }: SubscriptionCardProps) {
  if (!subscription) {
    return (
      <Card>
        <h3 className="text-lg font-semibold text-theme mb-4 flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          Suscripción
        </h3>
        <p className="text-theme-secondary text-center py-4">
          Sin suscripción activa
        </p>
      </Card>
    );
  }

  const badge = STATUS_BADGES[subscription.status] ?? { variant: 'default', label: subscription.status };

  return (
    <Card>
      <h3 className="text-lg font-semibold text-theme mb-4 flex items-center gap-2">
        <CreditCard className="w-5 h-5" />
        Suscripción
      </h3>

      <dl className="space-y-4">
        <div className="flex justify-between items-center">
          <dt className="text-theme-secondary">Estado</dt>
          <dd><Badge variant={badge.variant}>{badge.label}</Badge></dd>
        </div>

        {subscription.planName && (
          <div className="flex justify-between items-center">
            <dt className="text-theme-secondary">Plan</dt>
            <dd className="text-theme font-medium">{subscription.planName}</dd>
          </div>
        )}

        <div className="flex justify-between items-center">
          <dt className="text-theme-secondary">Período</dt>
          <dd className="text-theme capitalize">{subscription.billingCycle === 'MONTHLY' ? 'Mensual' : 'Anual'}</dd>
        </div>

        <div className="flex justify-between items-center">
          <dt className="text-theme-secondary flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            Próximo cobro
          </dt>
          <dd className="text-theme">
            {subscription.nextBillingDate ? new Date(subscription.nextBillingDate).toLocaleDateString() : '-'}
          </dd>
        </div>

        {subscription.trialEndsAt && (
          <div className="flex justify-between items-center">
            <dt className="text-theme-secondary">Trial termina</dt>
            <dd className="text-theme">{new Date(subscription.trialEndsAt).toLocaleDateString()}</dd>
          </div>
        )}

        {subscription.activeModules && subscription.activeModules.length > 0 && (
          <div>
            <dt className="text-theme-secondary mb-2 flex items-center gap-1">
              <Package className="w-4 h-4" />
              Módulos activos
            </dt>
            <dd className="flex flex-wrap gap-2">
              {subscription.activeModules.map((mod) => (
                <Badge key={mod} variant="default">{mod}</Badge>
              ))}
            </dd>
          </div>
        )}
      </dl>
    </Card>
  );
}
